import { Node, mergeAttributes, InputRule, type Editor } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { Node as PMNode } from "@tiptap/pm/model";
import i18n from "i18next";
import { MATH_MACROS_EVENT, renderLatex } from "@/lib/katex-config";

/**
 * Click-to-edit for inline maths (18 Oct: it could only be deleted and typed again). A small box
 * under the formula: its LaTeX, a live preview, Enter to apply, Esc to cancel, an emptied box
 * removes the formula. One at a time.
 */
let openEditor: { close: (commit: boolean) => void } | null = null;

function editInlineMath(editor: Editor, dom: HTMLElement, getPos: () => number | undefined, latex: string) {
  openEditor?.close(true);
  const box = document.createElement("div");
  box.className = "lilia-math-editor";
  box.setAttribute("data-testid", "inline-math-editor");
  box.setAttribute("role", "dialog");
  box.setAttribute("aria-label", i18n.t("editor:mathInline.label"));
  const input = document.createElement("input");
  input.type = "text";
  input.className = "lilia-math-editor-input";
  input.setAttribute("data-testid", "inline-math-input");
  input.setAttribute("aria-label", i18n.t("editor:mathInline.label"));
  input.spellcheck = false;
  input.value = latex;
  const preview = document.createElement("div");
  preview.className = "lilia-math-editor-preview";
  preview.setAttribute("data-testid", "inline-math-preview");
  const hint = document.createElement("div");
  hint.className = "lilia-math-editor-hint";
  hint.textContent = i18n.t("editor:mathInline.hint");
  box.append(input, preview, hint);
  const draw = () => {
    preview.innerHTML = input.value.trim() ? renderLatex(input.value, { displayMode: false, throwOnError: false }) : "";
  };
  draw();
  const rect = dom.getBoundingClientRect();
  box.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - 360))}px`;
  box.style.top = `${rect.bottom + 6}px`;
  document.body.appendChild(box);
  input.focus();
  input.select();

  // The formula's view can be rebuilt while the box is open (its getPos then answers nothing): its
  // place is taken now, and checked, or the same formula found nearest to it, when applying.
  const startPos = getPos();
  const findPos = (): number | undefined => {
    const doc = editor.state.doc;
    const here = getPos() ?? startPos;
    if (here !== undefined && doc.nodeAt(here)?.type.name === "math" && doc.nodeAt(here)?.attrs.latex === latex) return here;
    let best: number | undefined;
    doc.descendants((n, p) => {
      if (n.type.name === "math" && n.attrs.latex === latex
          && (best === undefined || Math.abs(p - (startPos ?? 0)) < Math.abs(best - (startPos ?? 0)))) best = p;
    });
    return best;
  };
  const outside = (e: MouseEvent) => { if (!box.contains(e.target as globalThis.Node)) close(true); };
  function close(commit: boolean) {
    if (openEditor?.close !== close) return;
    openEditor = null;
    document.removeEventListener("mousedown", outside, true);
    box.remove();
    const pos = findPos();
    if (!commit || pos === undefined) { editor.commands.focus(); return; }
    const value = input.value.trim();
    const node = editor.state.doc.nodeAt(pos);
    if (!node || node.type.name !== "math") return;
    if (!value) {
      editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
    } else if (value !== node.attrs.latex) {
      editor.chain().focus().command(({ tr }) => { tr.setNodeMarkup(pos, undefined, { ...node.attrs, latex: value }); return true; }).run();
    } else {
      editor.commands.focus();
    }
  }
  input.addEventListener("input", draw);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); close(true); }
    else if (e.key === "Escape") { e.preventDefault(); close(false); }
  });
  document.addEventListener("mousedown", outside, true);
  openEditor = { close };
}

export interface MathematicsOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mathematics: {
      /**
       * Insert inline math
       */
      insertMath: (latex: string) => ReturnType;
    };
  }
}

/**
 * TipTap extension for inline mathematics
 * Supports $...$ syntax for inline math
 */
export const Mathematics = Node.create<MathematicsOptions>({
  name: "math",
  group: "inline",
  inline: true,
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      latex: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-latex") || "",
        renderHTML: (attributes) => ({
          "data-latex": attributes.latex,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="math"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    // Fallback HTML for contexts that don't use the NodeView (e.g. SSR,
    // getHTML()). Rendered as a span with data-latex — the NodeView below
    // takes over in the editor. KaTeX content is NOT inlined here because
    // the ProseMirror tuple-renderer would stuff it into an HTML attribute
    // rather than as child nodes. For interactive editor display, see
    // `addNodeView` below which mounts a real DOM element with `.innerHTML`.
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "math",
        class: "inline-math",
      }),
      `$${node.attrs.latex as string}$`,
    ];
  },

  addNodeView() {
    return ({ node: initial, HTMLAttributes, editor, getPos }) => {
      let node: PMNode = initial;
      const dom = document.createElement("span");
      dom.setAttribute("data-type", "math");
      dom.setAttribute("data-testid", "inline-math");
      dom.setAttribute("contenteditable", "false");
      dom.className = "inline-math";
      for (const [k, v] of Object.entries(HTMLAttributes)) {
        if (k !== "data-type" && k !== "data-latex" && k !== "class") {
          dom.setAttribute(k, String(v));
        }
      }
      const draw = () => {
        const latex = (node.attrs.latex as string) ?? "";
        dom.setAttribute("data-latex", latex);
        try {
          dom.innerHTML = renderLatex(latex, { displayMode: false, throwOnError: false });
          dom.classList.remove("math-error");
        } catch {
          dom.textContent = `$${latex}$`;
          dom.classList.add("math-error");
        }
      };
      draw();
      // The document's macros arrive after the first draw, and change with its preamble.
      window.addEventListener(MATH_MACROS_EVENT, draw);
      // mousedown, not click: ProseMirror takes the press first (it selects the atom) and the click
      // never came through.
      dom.addEventListener("mousedown", (e) => {
        if (!editor.isEditable || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        editInlineMath(editor, dom, typeof getPos === "function" ? getPos : () => undefined, (node.attrs.latex as string) ?? "");
      });
      return {
        dom,
        update: (next) => {
          if (next.type !== node.type) return false;
          node = next;
          draw();
          return true;
        },
        destroy: () => window.removeEventListener(MATH_MACROS_EVENT, draw),
      };
    };
  },

  addCommands() {
    return {
      insertMath:
        (latex: string) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { latex },
          });
        },
    };
  },

  addInputRules() {
    const inline = (latex: string | undefined) => (latex && latex.trim() ? this.type.create({ latex: latex.trim() }) : null);
    return [
      // $…$, but not the first half of a $$…$$ and not an escaped \$.
      new InputRule({
        find: /(^|[^$\\])\$([^$\n]+)\$$/,
        handler: ({ state, range, match }) => {
          const mathNode = inline(match[2]);
          if (!mathNode) return null;
          // TipTap auto-dispatches the mutations applied to state.tr; return nothing.
          state.tr.replaceWith(range.from + match[1].length, range.to, mathNode);
        },
      }),
      // \(…\): LaTeX's own inline delimiters (18 Oct).
      new InputRule({
        find: /\\\(([^\n]+?)\\\)$/,
        handler: ({ state, range, match }) => {
          const mathNode = inline(match[1]);
          if (!mathNode) return null;
          state.tr.replaceWith(range.from, range.to, mathNode);
        },
      }),
      // $$…$$ and \[…\] alone in a paragraph: a displayed equation block (the Insert menu said
      // "Display math ($$…$$)" and typing it did nothing, 18 Oct). Inside a sentence, inline maths.
      new InputRule({
        find: /(?:\$\$([^$\n]+)\$\$|\\\[([^\n]+?)\\\])$/,
        handler: ({ state, range, match }) => {
          const latex = (match[1] ?? match[2] ?? "").trim();
          if (!latex) return null;
          const $from = state.doc.resolve(range.from);
          const parent = $from.parent;
          const equation = state.schema.nodes.equationBlock;
          // The rule runs before the last character lands: "alone" means the match starts the
          // paragraph and nothing follows the caret.
          const alone = $from.parentOffset === 0 && range.to === $from.end();
          if (equation && parent.type.name === "paragraph" && alone && $from.depth >= 1) {
            state.tr.replaceWith($from.before(), $from.after(), equation.create({ latex }));
            return;
          }
          const mathNode = inline(latex);
          if (mathNode) state.tr.replaceWith(range.from, range.to, mathNode);
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    // SG-101 / #104 fix: legacy content loaded from the API contains raw
    // `$...$` text instead of math atom nodes. The previous approach
    // decorated the text in place with a hidden source span +
    // KaTeX widget — but the source text remained a real cursor stop in
    // the document model, so users saw cursor "jumps":
    //   - ArrowRight from before-math advanced multiple positions at once
    //   - Typing inside the (display:none) source got bumped past the math
    //   - The hidden span inherited contenteditable=true from its parent
    //
    // Convert legacy text `$...$` into proper atom nodes on every doc
    // change via appendTransaction. Atom nodes have well-defined
    // boundaries — ArrowRight steps onto the math (NodeSelection), then
    // again past it; clicks land on the widget atomically. Same cursor
    // semantics as freshly-typed math (the InputRule path already
    // produced atoms — this brings loaded content to parity).
    // Not a $$…$$ pair (display, or being typed) and not an escaped \$.
    const mathRegex = /(?<![$\\])\$([^$\n]+)\$(?!\$)/g;
    const mathType = this.type;

    return [
      new Plugin({
        key: new PluginKey("mathTextToAtom"),
        // When the document's macros arrive, $\R$ written before parses now: look again.
        view: (view) => {
          const again = () => { if (!view.isDestroyed) view.dispatch(view.state.tr.setMeta("addToHistory", false)); };
          window.addEventListener(MATH_MACROS_EVENT, again);
          return { destroy: () => window.removeEventListener(MATH_MACROS_EVENT, again) };
        },
        appendTransaction: (_transactions, _oldState, newState) => {
          // Collect all text `$...$` matches across the doc, then apply
          // in reverse so earlier positions stay valid as later ones
          // shorten the document.
          const replacements: { from: number; to: number; latex: string }[] = [];
          // Not while the author is still writing it: Insert ▸ Inline math leaves the caret
          // inside `$ … $`, and converting as soon as the first character parsed threw the rest
          // out (typing x^2 gave $x$^2, 16 Oct). It converts once the caret leaves.
          const caret = newState.selection.empty ? newState.selection.from : -1;
          newState.doc.descendants((node, pos) => {
            if (!node.isText || !node.text) return;
            const text = node.text;
            mathRegex.lastIndex = 0;
            let m: RegExpExecArray | null;
            while ((m = mathRegex.exec(text)) !== null) {
              const latex = m[1].trim();
              if (!latex) continue;
              // Sanity-check: render attempt — if KaTeX can't parse it,
              // leave the text alone so the user sees their broken syntax
              // and can fix it in place. (SG-114 territory.)
              try {
                renderLatex(latex, { displayMode: false, throwOnError: true });
              } catch {
                continue;
              }
              const from = pos + m.index;
              const to = from + m[0].length;
              if (caret > from && caret < to) continue;
              replacements.push({ from, to, latex });
            }
          });

          if (replacements.length === 0) return null;
          const tr = newState.tr;
          for (const r of replacements.reverse()) {
            tr.replaceWith(r.from, r.to, mathType.create({ latex: r.latex }));
          }
          // Disable scroll-into-view side effect — these replacements
          // are silent migrations, not user edits.
          tr.setMeta("addToHistory", false);
          return tr;
        },
      }),
    ];
  },
});

export default Mathematics;
